
import React, { createContext, useContext } from 'react';
import { AuthUser } from '../types/AuthTypes';

// Define the context type
export interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  authResolved: boolean;
  hasSession: boolean;
  requiresMfa: boolean;
  requiresConsent: boolean;
  requiresDealerTerms: boolean;
  signIn: (email: string, password: string) => Promise<{ requiresMfa?: boolean }>;
  // GDPR: signUp now accepts privacy and marketing consent flags
  signUp: (email: string, password: string, firstName?: string, lastName?: string, privacyAccepted?: boolean, marketingConsent?: boolean) => Promise<{ requiresEmailConfirmation?: boolean } | void>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => boolean;
  completeMfaChallenge: () => Promise<void>;
  cancelMfa: () => Promise<void>;
  completeConsentChallenge: (marketingConsent: boolean) => Promise<void>;
  completeDealerTermsChallenge: () => Promise<void>;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: false,
  isAuthenticated: false,
  authResolved: false,
  hasSession: false,
  requiresMfa: false,
  requiresConsent: false,
  requiresDealerTerms: false,
  signIn: async () => ({}),
  signUp: async () => {},
  signOut: async () => {},
  hasRole: () => false,
  completeMfaChallenge: async () => {},
  cancelMfa: async () => {},
  completeConsentChallenge: async () => {},
  completeDealerTermsChallenge: async () => {},
});

// Custom hook for using the auth context
export const useAuthContext = () => useContext(AuthContext);

export default AuthContext;
